// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import {
  Badge, Button, Card, Empty, Field, Input, Loading, Modal, Select, TOKENS, Textarea,
  errorMessage, useToast,
} from "../ui";

// Phase 4 — 청소년 공의 의회(아젠다 ②). 안건 발의·조회·전이 + 투표(비운영자는 집계만).

const MOTION_FLOW: Record<string, string[]> = {
  draft: ["open", "rejected"],
  open:  ["closed"],
  closed:["adopted", "rejected"],
};

export function MotionsPanel({ isOperator, user }: { isOperator: boolean; user: any | null }) {
  const toast = useToast();
  const [status, setStatus] = React.useState("");
  const [list, setList] = React.useState<V.Motion[] | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [detail, setDetail] = React.useState<V.Motion | null>(null);

  const reload = React.useCallback(() => {
    V.motions.list({ status: status || undefined })
      .then((r) => setList(r.items))
      .catch((e) => { setList([]); toast.push(errorMessage(e), "danger"); });
  }, [status, toast]);
  React.useEffect(() => { reload(); }, [reload]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>청소년 공의 의회</h2>
        <div style={{ display: "flex", gap: 6 }}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 120 }}>
            <option value="">전체</option>
            {Object.entries(V.LABELS.motionStatus).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
          {user && <Button onClick={() => setCreating(true)}>안건 발의</Button>}
        </div>
      </div>

      {list === null ? <Loading /> :
       list.length === 0 ? <Empty title="조회 가능한 안건이 없습니다." hint="초안(draft) 상태 안건은 운영자만 볼 수 있습니다." /> :
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((m) => (
            <Card key={m.id} onClick={() => setDetail(m)}>
              <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {m.title}
                <Badge tone={toneForMotionStatus(m.status)}>{V.LABELS.motionStatus[m.status]}</Badge>
                {m.category && <Badge tone="neutral">{m.category}</Badge>}
              </div>
              <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 4, lineHeight: 1.6 }}>
                발의: {m.proposer_youth?.name || "-"} · {fmt(m.created_at)}
              </div>
            </Card>
          ))}
        </div>
      }

      <MotionCreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); reload(); }}
      />

      <MotionDetail
        open={!!detail}
        motion={detail}
        isOperator={isOperator}
        user={user}
        onClose={() => setDetail(null)}
        onChanged={() => { setDetail(null); reload(); }}
      />
    </div>
  );
}

function MotionCreateModal({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [proposerYouthId, setProposerYouthId] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTitle(""); setDescription(""); setCategory(""); setProposerYouthId(""); setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await V.motions.create({
        title: title.trim(),
        description: description || undefined,
        category: category || undefined,
        proposer_youth_id: proposerYouthId ? Number(proposerYouthId) : undefined,
      });
      toast.push("안건을 발의했습니다. 운영자가 투표 개시(open) 전까지 draft 상태로 유지됩니다.", "success");
      onCreated();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="공의 의회 안건 발의"
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting || !title.trim()}>
            {submitting ? "발의 중…" : "초안 발의"}
          </Button>
        </>
      }
    >
      <Field label="안건 제목" required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="카테고리">
        <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="예: 교통, 환경, 급식" />
      </Field>
      <Field label="발의 청소년 ID"
             hint="보호자인 경우 발의 명의 Youth ID. 비워 두면 본인(youth_council 트랙)으로 자동 조회.">
        <Input value={proposerYouthId} onChange={(e) => setProposerYouthId(e.target.value)} />
      </Field>
      <Field label="제안 설명">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
    </Modal>
  );
}

function MotionDetail({ open, motion, isOperator, user, onClose, onChanged }: {
  open: boolean; motion: V.Motion | null; isOperator: boolean; user: any | null;
  onClose: () => void; onChanged: () => void;
}) {
  const toast = useToast();
  const [tally, setTally] = React.useState<V.Tally | null>(null);
  const [items, setItems] = React.useState<any[] | null>(null);
  const [voting, setVoting] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const reload = React.useCallback(() => {
    if (!motion) return;
    V.votes.list(motion.id).then((r) => { setTally(r.tally); setItems(r.items || null); }).catch(() => {});
  }, [motion]);

  React.useEffect(() => { if (open) reload(); }, [open, reload]);

  if (!motion) return null;

  const nextStates = MOTION_FLOW[motion.status] || [];
  const transition = async (to: string) => {
    setBusy(true);
    try {
      await V.motions.transition(motion.id, to);
      toast.push(`${V.LABELS.motionStatus[to]} 상태로 전이했습니다.`, "success");
      onChanged();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally { setBusy(false); }
  };

  return (
    <Modal
      open={open}
      title={motion.title}
      onClose={onClose}
      width={640}
      footer={
        <>
          {isOperator && nextStates.length > 0 && (
            <div style={{ display: "flex", gap: 6 }}>
              {nextStates.map((to) => (
                <Button key={to} variant="secondary" size="sm" disabled={busy}
                  onClick={() => transition(to)}>
                  → {V.LABELS.motionStatus[to]}
                </Button>
              ))}
            </div>
          )}
          <div style={{ flex: 1 }} />
          {motion.status === "open" && user && <Button onClick={() => setVoting(true)}>투표</Button>}
          <Button variant="ghost" onClick={onClose}>닫기</Button>
        </>
      }
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <Badge tone={toneForMotionStatus(motion.status)}>{V.LABELS.motionStatus[motion.status]}</Badge>
        {motion.category && <Badge tone="neutral">{motion.category}</Badge>}
        {motion.proposer_youth && <Badge tone="info">발의: {motion.proposer_youth.name}</Badge>}
      </div>
      {motion.description && (
        <p style={{ fontSize: 13, lineHeight: 1.7, color: TOKENS.ink2, whiteSpace: "pre-wrap" }}>
          {motion.description}
        </p>
      )}

      <div style={{
        marginTop: 12, padding: 14, background: TOKENS.bgSub, borderRadius: 10,
        display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, textAlign: "center",
      }}>
        <TallyCell label="찬성" value={tally?.yes} tone={TOKENS.green600} />
        <TallyCell label="반대" value={tally?.no} tone={TOKENS.rose500} />
        <TallyCell label="기권" value={tally?.abstain} tone={TOKENS.ink3} />
      </div>

      {isOperator && items && (
        <>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: "16px 0 8px" }}>개별 투표 (운영자)</h3>
          {items.length === 0 ? <Empty title="투표 내역 없음" /> :
            <div style={{ display: "grid", gap: 4 }}>
              {items.map((v) => (
                <div key={v.id} style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "6px 10px", border: `1px solid ${TOKENS.borderSoft}`, borderRadius: 8, fontSize: 12,
                }}>
                  <span>{v.youth?.name || "-"}</span>
                  <Badge tone={v.choice === "yes" ? "success" : v.choice === "no" ? "danger" : "neutral"}>
                    {v.choice === "yes" ? "찬성" : v.choice === "no" ? "반대" : "기권"}
                  </Badge>
                </div>
              ))}
            </div>
          }
        </>
      )}

      <VoteModal
        open={voting}
        motion={motion}
        onClose={() => setVoting(false)}
        onVoted={() => { setVoting(false); reload(); }}
      />
    </Modal>
  );
}

function TallyCell({ label, value, tone }: { label: string; value: number | undefined; tone: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: TOKENS.ink3 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tone }}>{value ?? "-"}</div>
    </div>
  );
}

function VoteModal({ open, motion, onClose, onVoted }: {
  open: boolean; motion: V.Motion; onClose: () => void; onVoted: () => void;
}) {
  const toast = useToast();
  const [youthId, setYouthId] = React.useState("");
  const [choice, setChoice] = React.useState<"yes" | "no" | "abstain">("yes");
  const [reason, setReason] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setYouthId(""); setChoice("yes"); setReason(""); setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await V.votes.cast(motion.id, {
        youth_id: youthId ? Number(youthId) : undefined,
        choice,
        reason: reason || undefined,
      });
      toast.push("투표가 접수되었습니다.", "success");
      onVoted();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="투표"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "투표 중…" : "제출"}</Button>
        </>
      }
    >
      <Field label="투표 명의 Youth ID"
             hint="보호자 다중 연결이 있을 때. 비우면 본인 연결 youth_council 트랙 자동 조회.">
        <Input value={youthId} onChange={(e) => setYouthId(e.target.value)} />
      </Field>
      <Field label="선택">
        <Select value={choice} onChange={(e) => setChoice(e.target.value as any)}>
          <option value="yes">찬성</option>
          <option value="no">반대</option>
          <option value="abstain">기권</option>
        </Select>
      </Field>
      <Field label="사유 (선택)">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

function toneForMotionStatus(s: string): any {
  if (s === "open") return "primary";
  if (s === "adopted") return "success";
  if (s === "rejected") return "danger";
  if (s === "closed") return "info";
  return "warning"; // draft
}
function fmt(iso: string) { try { return new Date(iso).toLocaleDateString("ko-KR"); } catch { return iso; } }
